import { databaseEventKind, databaseEventZone } from './database_history_utils';
import { applySimulationHistoryProfile, createSimulationModel, initialSimulationZones, simulationDaytime } from './simulation_presets';

const minute = 60000;
const day = 24 * 60 * minute;
const fields = [
  ['temp', 1], ['humidity', 2], ['co2', 3], ['temp_leaf_current', 6],
  ['vol_water_content', 16], ['ec_root_zone', 15], ['temp_root_zone', 17]
];

// Read-only example history. Reuse the simulation's room targets and timing;
// no generated receipt, settings request or alarm is sent to the real server.
function sample(model, zone, at, anchor) {
  const scope = zone || '0'; // Non-zoned wire data uses the root; the simulator owns room 0.
  const settings = model.rooms[scope];
  const date = new Date(at);
  const values = applySimulationHistoryProfile({}, at, model, scope);
  const elapsed = (date.getHours() * 60 + date.getMinutes() -
    settings.timing.start_day_hour * 60 - settings.timing.start_day_min + 1440) % 1440;
  const daylight = simulationDaytime(settings, date);
  const irrigating = settings.irrigation?.irrigation_settings?.[0]?.enable_irrigation && daylight;
  const sinceFirst = elapsed - 90;
  const age = sinceFirst < 0 ? sinceFirst + 1440 - 7 * 60 : sinceFirst - Math.min(7, Math.floor(sinceFirst / 60)) * 60;
  const shot = irrigating && sinceFirst >= 0 && sinceFirst < 7 * 60 + 2 && sinceFirst % 60 < 2;
  const cycle = (date.getMinutes() + Number(zone) * 9) % 60;
  const humidifier = daylight && cycle >= 15 && cycle < 23;
  values.humidity += Math.round(daylight ? 15 * Math.sin((cycle - 17) / 60 * Math.PI * 2) : 0);
  // One recovered hot-room event and one individual-sensor dropout in the dataset.
  const hot = String(zone) === '1' && at >= anchor - day + 14 * 60 * minute && at < anchor - day + 14.75 * 60 * minute;
  if (hot) values.temp += Math.round(35 * Math.sin((at - (anchor - day + 14 * 60 * minute)) / (45 * minute) * Math.PI));
  values.temp_leaf_current = values.temp - 10;
  values.vol_water_content = Math.round(580 - Math.min(age, 1100) * 0.18 + Number(zone) * 12);
  values.ec_root_zone = Math.round(245 + (580 - values.vol_water_content) * 0.28);
  values.temp_root_zone = Math.round(values.temp * 0.25 + 165);
  values.vpd = Math.round(110 + (values.temp - 240) * 2);
  values.info_temp_sp = daylight ? settings.climate.temp_sp_day : settings.climate.temp_sp_night;
  values.info_lights = daylight ? (hot ? 55 : 80) : 0;
  values.ec_fans_exhaust_out = daylight ? (hot ? 85 : 55) : 30;
  values.ec_fans_exhaust_rpm = values.ec_fans_exhaust_out * 25;
  const nutrient = settings.nutrition.nutrient_settings[0];
  values.ec_nutrient_solution = nutrient.ec_setpoint + Math.round(3 * Math.sin(at / 900000));
  values.ph_nutrient_solution = nutrient.ph_setpoint + Math.round(5 * Math.cos(at / 1200000));
  values.temp_nutrient_solution = nutrient.temp_setpoint + Math.round(4 * Math.sin(at / 1500000));
  values.redox_current = 325 + Math.round(8 * Math.sin(at / 1200000));
  values.dos_current = 850 + Math.round(30 * Math.sin(at / 1800000));
  values.ec_nutrient_solution_target = nutrient.ec_setpoint;
  values.ph_nutrient_solution_target = nutrient.ph_setpoint;
  values.temp_nutrient_solution_target = nutrient.temp_setpoint;
  values.tank_level_percent = 85 - Math.round(elapsed / 1440 * 40);
  values.input_level = Math.round(4500 + 1500 * Math.sin(at / 1800000));
  values.output_level = values.ec_fans_exhaust_out * 100;
  const irrigationPhase = !settings.irrigation.irrigation_settings[0].enable_irrigation ? 0 :
    !daylight ? 3 : sinceFirst < 0 ? 0 : sinceFirst < 120 ? 1 : sinceFirst < 7 * 60 + 2 ? 2 : 3;
  values.irrigation_phase_info = irrigationPhase;
  values.irrigation_state = shot ? 4 : 0;
  return { values, phase: daylight ? 0 : 1, irrigationPhase, shot: shot ? 1 : 0, humidifier: humidifier ? 1 : 0 };
}

function aggregate(series, x, at, value) {
  let point = series.points[series.points.length - 1];
  if (!point || point.x !== x) {
    point = { x, y: null, min: null, max: null, count: 0, missing: 0, excluded_count: 0, first_at: at, last_at: at, last: null };
    series.points.push(point);
  }
  if (value === null) point.missing++;
  else {
    point.y = ((point.y ?? 0) * point.count + value) / (point.count + 1);
    point.count++;
    point.min = point.min === null ? value : Math.min(point.min, value);
    point.max = point.max === null ? value : Math.max(point.max, value);
  }
  point.last_at = at;
  point.last = value;
}

function transition(series, at, value, last) {
  if (!series.points.length || series.points[series.points.length - 1].y !== value || last)
    series.points.push({ x: at, y: value });
}

function irrigationTotals(series, period) {
  return series.filter((item) => item.kind === 'shots').flatMap((shots) => {
    const phase = series.find((item) => item.kind === 'irrigation_phase' && item.zone === shots.zone && item.subZone === shots.subZone);
    if (!phase) return [];
    const intervals = (points) => points.flatMap((point, index) => {
      const next = points[index + 1];
      return next && point.y !== null && next.y !== null ? [{ from: point.x, to: next.x, value: point.y }] : [];
    });
    const shotIntervals = intervals(shots.points), phaseIntervals = intervals(phase.points);
    const summarize = (from, to) => {
      const phases = [0, 1, 2, 3].map((phase) => ({ phase, duration_ms: 0 }));
      let total_ms = 0, observed_ms = 0, unknown_phase_ms = 0;
      for (const shot of shotIntervals) {
        const start = Math.max(from, shot.from), end = Math.min(to, shot.to);
        if (end <= start || ![0, 1].includes(shot.value)) continue;
        observed_ms += end - start;
        if (shot.value !== 1) continue;
        total_ms += end - start;
        let assigned = 0;
        for (const phase of phaseIntervals) {
          if (![0, 1, 2, 3].includes(phase.value)) continue;
          const overlap = Math.max(0, Math.min(end, phase.to) - Math.max(start, phase.from));
          phases[phase.value].duration_ms += overlap;
          assigned += overlap;
        }
        unknown_phase_ms += end - start - assigned;
      }
      return { total_ms, observed_ms, unknown_ms: to - from - observed_ms, unknown_phase_ms, phases };
    };
    const days = [];
    const date = new Date(period.from); date.setHours(0, 0, 0, 0);
    while (date.getTime() < period.to) {
      const from = Math.max(period.from, date.getTime());
      const day = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
      date.setDate(date.getDate() + 1);
      const to = Math.min(period.to, date.getTime());
      days.push({ day, from, to, ...summarize(from, to) });
    }
    return [{ zone: shots.zone, subZone: shots.subZone, ...summarize(period.from, period.to), days }];
  });
}

export function simulationDatabaseHistory(controller, query, now = Date.now()) {
  const zones = controller.type === 5155
    ? controller.simulation?.zones ?? (Object.keys(controller.zones ?? {}).length
      ? Object.values(controller.zones) : initialSimulationZones(controller.type))
    : [{ id: '', name: controller.name ?? '' }];
  const model = controller.simulation?.model ?? createSimulationModel(controller.type, zones);
  const midnight = new Date(now); midnight.setHours(0, 0, 0, 0);
  const anchor = midnight.getTime();
  const availableFrom = anchor - 13 * day;
  const availableTo = Math.floor(now / minute) * minute;
  const reply = { request_id: query.request_id, status: 'ok', failed_inserts: 0, next_before_id: null };
  if (query.view !== 'measurements') return { ...reply, ...receipts(model, zones, query, availableFrom, availableTo, anchor) };

  const period = query.period;
  const from = Math.max(period.from, availableFrom);
  const to = Math.min(period.to, availableTo);
  const selectedZones = zones.filter((zone) => query.zone === 'all' || String(zone.id) === String(query.zone));
  const series = [];
  const selected = [];
  for (const zone of selectedZones) {
    const id = String(zone.id);
    for (const [field, type] of fields.filter(([, type]) => type < 15 || [5055, 5155].includes(controller.type))) {
      const main = { id: `example:${id}:${field}`, zone: id, name: zone.name, kind: 'main', field, subZone: 0, points: [] };
      series.push(main);
      const useMain = !query.main_fields?.length || query.main_fields.includes(field);
      if (useMain) selected.push({ series: main, field, zone: id });
      for (let sensor = 0; sensor < 2; sensor++) {
        const item = { id: `example:${id}:${field}:${sensor}`, zone: id, name: zone.name, kind: 'sensor', type,
          sub_zone: 0, gcl_addr: Number(id), port_id: sensor, addr_sensor: 48 + sensor, sensor_addr_display_type: 0, points: [] };
        series.push(item);
        if (query.include_sensors) selected.push({ series: item, field, zone: id, sensor });
      }
    }
    series.push({ id: `example:${id}:phase`, zone: id, name: zone.name, kind: 'phase', points: [] });
    if (query.include_equipment) for (const [field, label, unit, scale, group] of [
      ['vpd', 'vpd', 'kPa', 0.01, 'climate'],
      ['info_temp_sp', 'database_temperature_target', '°C', 0.1, 'climate'],
      ['info_lights', 'database_light_level', '%', 1, 'lighting'],
      ['ec_fans_exhaust_out', 'database_exhaust_level', '%', 1, 'ventilation'],
      ['ec_fans_exhaust_rpm', 'database_exhaust_rpm', 'rpm', 1, 'ventilation']
    ]) {
      const item = { id: `example:${id}:${field}`, zone: id, name: zone.name, kind: 'metric',
        field, label, unit, scale, group, points: [], discrete: field === 'info_temp_sp' };
      series.push(item);
      selected.push({ series: item, field, zone: id });
    }
    if (query.include_equipment && controller.type === 5155) {
      // Explicit example ports, alongside the existing example humidifier DO.
      for (const input of [true, false]) {
        const field = input ? 'input_level' : 'output_level';
        const item = { id: `example:${id}:${field}`, zone: id, name: zone.name, kind: 'metric',
          field, label: input ? 'database_input_level' : 'database_output_level', unit: '%', scale: 0.01,
          group: input ? 'inputs' : 'outputs', address: Number(id), port: input ? 4 : 3, memo: input ? 'AI' : 'AO', points: [] };
        series.push(item); selected.push({ series: item, field, zone: id });
      }
    }
    if (query.include_equipment && [5055, 5155].includes(controller.type)) {
      const tank = model.rooms[id || '0'].irrigation_tank_info[0];
      if (tank) for (const [field, unit, scale] of [
        ['ec_nutrient_solution', 'mS/cm', 0.01], ['ph_nutrient_solution', 'pH', 0.01],
        ['temp_nutrient_solution', '°C', 0.1], ['redox_current', 'mV', 1], ['dos_current', '%', 0.1], ['tank_level_percent', '%', 1]
      ]) {
        const item = { id: `example:${id}:tank:${tank.tank_id}:${field}`, zone: id, name: zone.name, kind: 'metric',
          field, label: field === 'tank_level_percent' ? 'tank_level' : field, unit, scale, group: 'fertigation',
          tank_id: tank.tank_id, tank_name: tank.name, points: [] };
        series.push(item); selected.push({ series: item, field, zone: id });
        if (['ec_nutrient_solution', 'ph_nutrient_solution', 'temp_nutrient_solution'].includes(field)) {
          const target = { ...item, id: item.id + ':target', field: field + '_target', target: true, discrete: true, points: [] };
          series.push(target); selected.push({ series: target, field: target.field, zone: id });
        }
      }
    }
    if (query.include_activities) series.push(
      { id: `example:${id}:shots`, zone: id, name: zone.name, kind: 'shots', subZone: 0, points: [] },
      { id: `example:${id}:humidifier`, zone: id, name: zone.name, kind: 'output', address: Number(id), port: 2, func: 2, memo: '', points: [] });
    if (query.include_activities && [5055, 5155].includes(controller.type))
      series.push({ id: `example:${id}:irrigation-phase`, zone: id, name: zone.name, kind: 'irrigation_phase', subZone: 0, points: [] });
  }
  const budget = Math.max(1, Math.min(query.points ?? 600, Math.floor(12000 / Math.max(1, selected.length))));
  const resolution = Math.max(minute, Math.ceil((period.to - period.from) / budget / minute) * minute);
  for (const zone of selectedZones) {
    const id = String(zone.id);
    const readings = selected.filter((item) => item.zone === id);
    const states = series.filter((item) => item.zone === id && ['phase', 'shots', 'output', 'irrigation_phase'].includes(item.kind));
    for (let at = Math.ceil(from / minute) * minute; at < to; at += minute) {
      const current = sample(model, id, at, anchor);
      for (const item of readings) {
        let value = current.values[item.field];
        if (item.sensor !== undefined) {
          const offset = Math.round(Math.sin(at / 1200000 + Number(id)) * (item.field === 'co2' ? 22 : 6));
          value += item.sensor ? -offset : offset;
          if (item.sensor === 1 && at >= anchor - day + 11 * 60 * minute && at < anchor - day + 11.5 * 60 * minute) value = null;
        }
        if (item.series.discrete) transition(item.series, at, value, at + minute >= to);
        else aggregate(item.series, Math.floor(at / resolution) * resolution, at, value);
      }
      for (const state of states) transition(state, at,
        current[{ phase: 'phase', shots: 'shot', output: 'humidifier', irrigation_phase: 'irrigationPhase' }[state.kind]], at + minute >= to);
    }
  }
  // Compute the complete observed intervals before limiting graph transitions.
  const irrigation_totals = irrigationTotals(series, period);
  let remaining = 6000;
  let limited = false;
  for (const state of series.filter((item) => ['phase', 'shots', 'output', 'irrigation_phase'].includes(item.kind))) {
    if (state.points.length > remaining) { state.points = state.points.slice(0, remaining); limited = true; }
    remaining -= state.points.length;
  }
  const observed = selected.filter((item) => !item.series.discrete).flatMap((item) => item.series.points);
  return { ...reply, query_version: 1, period, resolution_ms: resolution, series, irrigation_totals,
    coverage: { first: observed.length ? Math.min(...observed.map((p) => p.first_at)) : null,
      last: observed.length ? Math.max(...observed.map((p) => p.last_at)) : null },
    indexing: false, projection_error: false, limited };
}

function receipts(model, zones, query, from, to, anchor) {
  const rows = [];
  const add = (at, code, payload) => {
    if (at >= from && at < to) rows.push({ id: String(at * 100 + code), received_at: at, payload });
  };
  for (const [index, zone] of zones.entries()) {
    const id = String(zone.id);
    for (let at = from; at < to; at += 60 * minute) {
      const values = { cur_values: sample(model, id, at, anchor).values };
      add(at, index, { command: 'update_values', data: { timestamp: at,
        ...(id === '' ? values : { zones: [{ id, name: zone.name, settings: values }] }) } });
    }
    for (let date = from; date < to; date += day) {
      add(date + 9.5 * 60 * minute, 20 + index, { command: 'settings_request',
        send_status: 'queued', controller_confirmation: 'unavailable',
        actor: { username: index % 2 ? 'Demo team' : 'Demo grower', kind: index % 2 ? 'subuser' : 'owner' },
        data: { ...(id === '' ? {} : { id, name: zone.name, has_zone_data: true }),
          climate: { temp_sp_day: model.rooms[id || '0'].climate.temp_sp_day } } });
      add(date + 10 * 60 * minute, 40 + index, { command: 'message', data: {
        timestamp: date + 10 * 60 * minute, type: 'info', message_parts: [zone.name, ' · ', 'database_demo_irrigation'] } });
    }
  }
  const room = zones.find((zone) => String(zone.id) === '1') ?? zones[0];
  if (room) {
    add(anchor - day + 14 * 60 * minute, 80, { command: 'message', data: {
      timestamp: anchor - day + 14 * 60 * minute, type: 'warning', message_parts: [room.name, ' · ', 'database_demo_temperature_alarm'] } });
    add(anchor - day + 14.75 * 60 * minute, 81, { command: 'message', data: {
      timestamp: anchor - day + 14.75 * 60 * minute, type: 'info', message_parts: [room.name, ' · ', 'database_demo_temperature_recovered'] } });
  }
  const events = query.view === 'events';
  const matches = rows.filter((row) => (!events || (['message', 'settings_request', 'update_alarms'].includes(row.payload.command) &&
    row.received_at >= query.period.from && row.received_at < query.period.to &&
    (!query.event_type || query.event_type === 'all' || databaseEventKind(row) === query.event_type) &&
    (query.zone === 'all' || databaseEventZone(row) === query.zone) &&
    (!query.actor || row.payload.actor?.username === query.actor))) && (!query.command || row.payload.command === query.command) &&
    (!query.before_id || Number(row.id) < Number(query.before_id))).sort((a, b) => Number(b.id) - Number(a.id));
  const limit = events ? 1000 : 100;
  return { rows: matches.slice(0, limit), next_before_id: matches.length > limit ? matches[limit - 1].id : null,
    ...(events ? { events_query_version: 1, period: query.period, limited: matches.length > limit } : {}) };
}
